/**
 * Stream tickets API client.
 *
 * The customer-facing SSE endpoint (`GET /api/stream/stream/sessions/:uid`)
 * accepts three auth modes: JWT-bearer + owned session, HttpOnly guest
 * recovery cookie, and short-lived `?ticket=` from this endpoint. Native
 * EventSource can attach neither an Authorization header nor a cross-
 * origin cookie in every browser environment, so we mint a fresh single-
 * use ticket and append it to the URL for each connect / reconnect.
 *
 * Contract (see sw-ev-api/routes/stream-tickets.js + streamTicketsController.js):
 *   POST /api/stream/tickets  (authRequired)
 *   body: { streamType: 'session', sessionUid: '<uuid>' }
 *   200:  { ok: true, ticket: '<raw>', expiresAt: '<iso>' }
 *   401:  caller is unauthenticated (guest / expired JWT)  → this client
 *          returns { ok:false, status:401 } so the SSE consumer can fall
 *          back to the polling loop without mistaking it for a transient
 *          network error.
 *   403/404: caller does not own the session               → same fallback.
 *   5xx / network: leave the SSE consumer's normal retry path alone.
 *
 * The raw ticket is NEVER logged. Only its presence + expiry are ever
 * observed here.
 */

import { authClient } from '../auth/authClient.js';

/**
 * Mint a session-scoped stream ticket for the given session UID.
 * Returns { ok:true, ticket, expiresAt } on success, or a structured
 * failure { ok:false, status, error } — never throws for expected
 * auth-boundary responses so callers can branch cleanly.
 */
export async function mintSessionStreamTicket(sessionUid) {
    if (typeof sessionUid !== 'string' || sessionUid.length === 0) {
        return { ok: false, status: 400, error: 'INVALID_SESSION_UID' };
    }
    try {
        const { data } = await authClient.post('/stream/tickets', {
            streamType: 'session',
            sessionUid,
        }, {
            // Send the HttpOnly session recovery cookie so a GUEST (no user
            // JWT) can mint a ticket for their own session. An authenticated
            // user still authenticates via the Bearer the interceptor adds.
            withCredentials: true,
        });
        const ticket = data?.ticket;
        if (typeof ticket !== 'string' || ticket.length === 0) {
            return { ok: false, status: 502, error: 'TICKET_MINT_EMPTY' };
        }
        return {
            ok: true,
            ticket,
            expiresAt: data?.expiresAt || null,
        };
    } catch (err) {
        const status = err?.response?.status || null;
        const error = err?.response?.data?.error || 'TICKET_MINT_FAILED';
        // Deliberately do not include the request body or the response
        // headers in this log line.
        return { ok: false, status, error };
    }
}

/**
 * Terminal codes from mint that should stop the SSE consumer from
 * retrying. 401 = caller is unauthenticated (guest / expired JWT).
 * 403 = caller is authenticated but does not own the session. 404 = the
 * server refuses to acknowledge the session, treated as ownership-fail
 * per the mint controller's spec.
 */
export function isTerminalMintStatus(status) {
    return status === 401 || status === 403 || status === 404;
}
